/**
 * Public Digest Directory — /digest/featured
 * - Hero, filter bar (industry / sort / time range)
 * - 3-column grid of digest cards with badges
 * - SEO meta tags
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, TrendingUp, Clock, Volume2, Eye, Share2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  { id: null, label: 'All' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'technology', label: 'Technology' },
  { id: 'business', label: 'Business' },
  { id: 'ecommerce', label: 'E-commerce' },
  { id: 'health', label: 'Health' },
];

const SORTS = [
  { id: 'most_viewed', label: 'Most Viewed' },
  { id: 'most_shared', label: 'Most Shared' },
  { id: 'most_recent', label: 'Most Recent' },
  { id: 'trending', label: 'Trending (24h)' },
];

const RANGES = [
  { id: 'this_week', label: 'This week' },
  { id: 'this_month', label: 'This month' },
  { id: 'all_time', label: 'All time' },
];

const BADGE_STYLES = {
  featured_creator: { label: '⭐ Featured', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  trending: { label: '🔥 Trending', cls: 'bg-red-500/20 text-red-300 border-red-500/30' },
  new: { label: '✨ New', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
};

const DigestFeaturedPage = () => {
  const [items, setItems] = useState([]);
  const [industryCounts, setIndustryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState(null);
  const [sort, setSort] = useState('most_viewed');
  const [range, setRange] = useState('all_time');

  useEffect(() => {
    document.title = 'Competitive Intelligence Digests — Weekly Industry Trends & Analysis | iSupplyAI';
    // SEO meta
    const meta = document.createElement('meta');
    meta.name = 'description';
    meta.content = 'Browse AI-generated weekly intelligence digests covering marketing, tech, business, and more. See what trends your competitors are missing.';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ sort, range });
        if (industry) params.set('industry', industry);
        const { data } = await axios.get(`${API_URL}/api/public/digest/featured?${params}`);
        if (!cancelled) {
          setItems(data.items || []);
          setIndustryCounts(data.industry_counts || {});
        }
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [industry, sort, range]);

  const totalAcrossIndustries = useMemo(
    () => Object.values(industryCounts).reduce((a, b) => a + (b || 0), 0),
    [industryCounts]
  );

  return (
    <div className="min-h-screen bg-[#020617] text-white" data-testid="digest-featured-page">
      {/* Top brand bar */}
      <div className="border-b border-gray-800 bg-gray-900/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white font-bold text-sm">
            <span>🧠</span>
            <span>iSupplyAI <span className="text-purple-400 font-normal">Pulse</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/?auth=login" className="text-xs px-3 py-1.5 rounded-lg text-gray-300 hover:text-white border border-gray-700 hover:border-gray-500" data-testid="featured-login-btn">
              Login
            </Link>
            <Link to="/" className="text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold">
              Sign up →
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="border-b border-gray-800 bg-gradient-to-b from-purple-900/20 via-gray-950 to-gray-950">
        <div className="max-w-5xl mx-auto px-4 py-14 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 text-purple-300 text-xs font-bold uppercase tracking-widest border border-purple-500/20 mb-4">
            <Sparkles className="w-3 h-3" /> The Intelligence Directory
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4 bg-gradient-to-br from-white via-gray-100 to-purple-300 bg-clip-text text-transparent">
            🧠 Intelligence Digest Directory
          </h1>
          <p className="text-base text-gray-300 max-w-2xl mx-auto font-medium">
            Real-time competitive intelligence from industry leaders.
          </p>
          <p className="text-sm text-gray-400 max-w-2xl mx-auto mt-3">
            Every Monday, iSupplyAI users receive AI-generated intelligence digests. The best ones are shared here —
            covering {totalAcrossIndustries || 'multiple'} industries, with 60-second CEO audio briefings included.
          </p>
          <div className="mt-6">
            <Link to="/" className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-sm" data-testid="digest-featured-hero-cta">
              Create your own digest — Free →
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="rounded-xl bg-gray-900/40 border border-gray-800 p-4 flex flex-wrap items-center gap-3" data-testid="digest-featured-filters">
          {/* Industry */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 uppercase tracking-widest">Industry:</span>
            {INDUSTRIES.map(ind => (
              <button
                key={ind.id || 'all'}
                onClick={() => setIndustry(ind.id)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                  industry === ind.id
                    ? 'bg-purple-500/30 text-purple-200 border-purple-400'
                    : 'bg-transparent text-gray-400 border-gray-700 hover:text-white hover:border-gray-600'
                }`}
                data-testid={`digest-filter-industry-${ind.id || 'all'}`}
              >
                {ind.label}
                {ind.id && industryCounts[ind.id] !== undefined && (
                  <span className="ml-1 text-[10px] text-gray-500">({industryCounts[ind.id]})</span>
                )}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none"
              data-testid="digest-filter-sort"
            >
              {SORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none"
              data-testid="digest-filter-range"
            >
              {RANGES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading digests…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 rounded-2xl border border-gray-800 bg-gray-900/40">
            <div className="text-5xl mb-3">📭</div>
            <div className="text-lg font-bold text-white">No digests match these filters yet</div>
            <div className="text-sm text-gray-400 mt-2">Try a different industry or time range — or publish yours first.</div>
            <Link to="/?settings=digest" className="inline-block mt-5 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-semibold">
              Publish your digest →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="digest-featured-grid">
            {items.map((it, i) => {
              const href = `/digest/@${it.username}/${it.week_slug}`;
              const archive = `/digest/@${it.username}`;
              return (
                <Link
                  to={href}
                  key={`${it.username}-${it.week_slug}`}
                  className="group rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-gray-950/80 hover:border-purple-500/40 transition-all overflow-hidden flex flex-col"
                  data-testid={`digest-card-${i}`}
                >
                  {/* Gradient header */}
                  <div className="h-2 bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-400" />

                  <div className="p-5 flex-1 flex flex-col">
                    {/* Badges */}
                    {(it.badges || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {it.badges.map(b => BADGE_STYLES[b] && (
                          <span key={b} className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${BADGE_STYLES[b].cls}`}>
                            {BADGE_STYLES[b].label}
                          </span>
                        ))}
                        <span className="ml-auto text-[10px] text-gray-500 uppercase tracking-widest font-semibold">
                          {it.industry || 'general'}
                        </span>
                      </div>
                    )}

                    {/* Title */}
                    <h3 className="text-lg font-bold text-white leading-snug mb-2 group-hover:text-purple-300 transition-colors">
                      {(it.user_name || it.username || 'Anonymous').split(' ')[0]}'s Week Ahead
                    </h3>

                    {/* Topics preview */}
                    {(it.topics_preview || []).length > 0 ? (
                      <ul className="space-y-1 mb-4 flex-1">
                        {it.topics_preview.map((t, j) => (
                          <li key={j} className="text-sm text-gray-400 flex gap-2">
                            <span className="text-purple-400 flex-shrink-0">#{j + 1}</span>
                            <span className="line-clamp-1">{t}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-sm text-gray-500 italic mb-4 flex-1">Digest coming together…</div>
                    )}

                    {/* Meta row */}
                    <div className="flex items-center gap-3 text-[11px] text-gray-500 pt-3 border-t border-gray-800/60">
                      {it.audio_included && (
                        <span className="flex items-center gap-1 text-amber-400/80">
                          <Volume2 className="w-3 h-3" /> 60s
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {it.view_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Share2 className="w-3 h-3" /> {it.share_count || 0}
                      </span>
                      <span className="ml-auto truncate" onClick={(e) => { e.preventDefault(); window.location.href = archive; }}>@{it.username}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="border-t border-gray-800 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="flex items-center justify-center gap-2 text-purple-300 text-xs font-bold uppercase tracking-[0.25em] mb-3">
            <TrendingUp className="w-3.5 h-3.5" /> Powered by iSupplyAI
          </div>
          <h2 className="text-2xl sm:text-3xl font-black mb-3">Get your own weekly digest</h2>
          <p className="text-gray-400 max-w-md mx-auto mb-5 text-sm">
            Join the operators getting a 60-second CEO briefing + 3 uncovered trends + 1 ready-to-forge brief every Monday at 9am local time.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold shadow-lg shadow-purple-500/30"
            data-testid="digest-featured-signup-cta"
          >
            Start free →
          </Link>
          <div className="text-[11px] text-gray-600 mt-6 flex items-center justify-center gap-1.5">
            <Clock className="w-3 h-3" /> New digests publish every Monday
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigestFeaturedPage;
